import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, LayerGroup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '../Projects.css';

import { useProjectMap } from './hooks/useProjectMap';
import { createCustomIcon, createClusterIcon } from './utils/mapIcons';
import { MapSidebar } from './components/MapSidebar';
import { ProjectPopupContent, openMultiProjectPopup } from './components/MapPopups';
import { MapBoundsHandler, MapFocuser, ResetZoomControl, MapLegend } from './components/MapControls';

/**
 * @file ProjectOverviewMap.jsx
 * @description The main map component for project overview.
 * Modularized according to the Gold Standard.
 * 
 * @param {Object} props
 * @param {Array} props.projects - List of all projects
 * @param {Function} props.onProjectSelect - Detail callback
 * @param {Function} props.onProjectEdit - Edit callback
 * @param {Object} props.focusedProject - Project to zoom into
 * @param {Function} props.onFocusCleared - Focus reset callback
 */
export default function ProjectOverviewMap({ 
    projects, 
    onProjectSelect, 
    onProjectEdit, 
    focusedProject, 
    onFocusCleared 
}) {
    const {
        isSidebarOpen, setIsSidebarOpen,
        projectSearch, setProjectSearch,
        selectedProjectIds,
        expandedGroups,
        projectsWithGps,
        filteredForMap,
        groupedProjects,
        sidebarProjects,
        toggleGroup,
        toggleGroupProjects,
        toggleProject,
        selectAll,
        deselectAll
    } = useProjectMap({ projects, focusedProject });

    // Empty state if no GPS data available
    if (projectsWithGps.length === 0) {
        return (
            <div className="empty-map-state" style={{
                height: '500px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                color: '#6b7280'
            }}>
                <span style={{ fontSize: '3rem', marginBottom: '10px' }}>📍</span>
                <p>Keine Projekte mit Standortdaten gefunden.</p>
                <p style={{ fontSize: '0.85rem' }}>Füge Standorte in den Projektdetails hinzu, um sie hier zu sehen.</p>
            </div>
        );
    }

    return (
        <div className="map-view-container" style={{ width: '100%', position: 'relative' }}>
            {!isSidebarOpen && (
                <button
                    className="map-sidebar-toggle-btn"
                    onClick={() => setIsSidebarOpen(true)}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                    Projekte auswählen ({selectedProjectIds.length}/{projectsWithGps.length})
                </button>
            )}

            <MapSidebar 
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
                projectSearch={projectSearch}
                setProjectSearch={setProjectSearch}
                selectedProjectIds={selectedProjectIds}
                groupedProjects={groupedProjects}
                expandedGroups={expandedGroups}
                toggleGroup={toggleGroup}
                toggleGroupProjects={toggleGroupProjects}
                toggleProject={toggleProject}
                selectAll={selectAll}
                deselectAll={deselectAll}
                totalGpsCount={projectsWithGps.length}
            />

            <MapContainer
                center={[51.1657, 10.4515]}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
            >
                <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Karte (Standard)">
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Satellit">
                        <LayerGroup>
                            <TileLayer
                                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EBP, and the GIS User Community'
                            />
                            <TileLayer
                                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                                attribution='&copy; Esri &mdash; Labels'
                            />
                        </LayerGroup>
                    </LayersControl.BaseLayer>
                </LayersControl>

                <MarkerClusterGroup
                    chunkedLoading
                    iconCreateFunction={createClusterIcon}
                    spiderfyOnMaxZoom={false}
                    eventHandlers={{
                        clusterclick: (e) => {
                            const cluster = e.layer;
                            const map = e.target._map;
                            if (!(cluster instanceof L.MarkerCluster)) return;

                            const currentZoom = map.getZoom();
                            const maxZoom = map.getMaxZoom();
                            const markers = cluster.getAllChildMarkers();
                            const projectsInCluster = markers.map(m => m.options.projectData).filter(Boolean);
                            const firstLatLng = markers[0].getLatLng();
                            const allSameLocation = markers.every(m => m.getLatLng().equals(firstLatLng, 0.00001));

                            if (currentZoom >= maxZoom || allSameLocation) {
                                openMultiProjectPopup(map, cluster.getLatLng(), projectsInCluster, onProjectSelect, onProjectEdit);
                            }
                        }
                    }}
                >
                    {filteredForMap.map(project => (
                        <Marker
                            key={project.id}
                            position={[project.latitude, project.longitude]}
                            icon={createCustomIcon(project.status)}
                            projectStatus={project.status}
                            projectData={project}
                        >
                            <Popup>
                                <ProjectPopupContent
                                    project={project}
                                    onProjectSelect={onProjectSelect}
                                    onProjectEdit={onProjectEdit}
                                />
                            </Popup>
                        </Marker>
                    ))}
                </MarkerClusterGroup>

                <MapBoundsHandler projects={filteredForMap} isFocusing={!!focusedProject} />
                <MapFocuser focusedProject={focusedProject} onCleared={onFocusCleared} />
                <ResetZoomControl projects={filteredForMap} />
            </MapContainer>

            <MapLegend />
        </div>
    );
}
